import { type EnhancedComponentExtendedProps } from '@ui/enhanced-components/types'
import { type GatsbyLinkProps } from 'gatsby'
import queryString from 'query-string'
import React, {
  type AnchorHTMLAttributes,
  type ComponentType,
  type ElementType,
  type FC,
  useContext,
  useEffect,
  useState,
} from 'react'

import { TokenContext } from './token-context'
import { useIsIframe } from './use-is-iframe'

type HTMLAttributesExtended = Partial<GatsbyLinkProps<any>> &
  AnchorHTMLAttributes<any> & {
    component?: ElementType
  }

export const withEspaceClientLinkSupport =
  <EnhancedComponentProps extends EnhancedComponentExtendedProps>(
    Component: ComponentType<EnhancedComponentProps>,
    isSubscriptionLink: (path: string) => boolean,
    isExternal: (url: string) => boolean
  ): FC<EnhancedComponentProps & HTMLAttributesExtended> =>
  (props) => {
    const { href, to } = props
    const isIframe = useIsIframe()
    const isGatsbyLink = Boolean(to)
    const [newHref, setNewHref] = useState(to || href)
    const token = useContext(TokenContext)

    useEffect(() => {
      setNewHref(to || href)
    }, [to, href])

    useEffect(() => {
      if (isIframe && newHref) {
        if (isSubscriptionLink(newHref) && token) {
          setNewHref(
            queryString.stringifyUrl({
              url: newHref,
              query: {
                token,
              },
            })
          )
        } else if (!isExternal(newHref)) {
          setNewHref(
            queryString.stringifyUrl({
              url: newHref,
              query: {
                iframe: true,
              },
            })
          )
        }
      }
    }, [isIframe, href, to, token, newHref])

    const newProps = isGatsbyLink
      ? { to: newHref, href: newHref }
      : { href: newHref }

    return <Component {...props} {...newProps} />
  }
